"use client";

import { trackMerchantEvent as track } from "@/lib/analytics/client";
import { useOnboardingSteps } from "@/hooks/use-onboarding-steps";
import { subscribeToOnboardingStream } from "@/services/onboarding";
import { onboardingProps, useOnboardingStore } from "@/store/onboardingStore";
import {
  getOnboardingStepPath,
  getStepNameFromPathname,
} from "@/utils/onboarding";
import {
  ONBOARDING_STEPS,
  OnboardingResponseState,
  PROGRESS_EVENT_TYPE,
} from "@jurni/schemas";
import { PropsWithChildren, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useRouter } from "next/navigation";
import { useOnboardingAnalyticsContext } from "../(onboarding-layout)/components/onboarding-analytics-context";

interface OnboardingProviderProps extends PropsWithChildren {
  onboardingState: OnboardingResponseState;
}

export const OnboardingProvider = ({
  children,
  onboardingState,
}: OnboardingProviderProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { goNext } = useOnboardingSteps();
  const lastViewedStep = useRef<string | null>(null);
  const updateOnboardingState = useOnboardingStore(
    onboardingProps.updateOnboardingState,
  );

  const resetOnboardingState = useOnboardingStore(
    onboardingProps.resetOnboardingState,
  );
  const stepNameFromPath = getStepNameFromPathname(pathname);

  useOnboardingAnalyticsContext(onboardingState.onboardingId);

  useEffect(() => {
    updateOnboardingState(onboardingState);

    const abortController = new AbortController();

    const subscribeToStream = async () => {
      try {
        const stream = await subscribeToOnboardingStream(
          abortController.signal,
        );
        if (!stream) return;

        for await (const event of stream) {
          if (abortController.signal.aborted) return;

          const { type, state } = event.message;

          if (type === PROGRESS_EVENT_TYPE.WORKFLOW_COMPLETED) {
            track("onboarding_flow_completed", {
              onboarding_id: onboardingState.onboardingId,
            });
            window.location.replace("/home");
            return;
          }

          if (state.currentStepName === ONBOARDING_STEPS.FINALIZING) {
            continue;
          }

          switch (type) {
            case PROGRESS_EVENT_TYPE.SUBMIT_DONE:
              const { flow } = event.message;
              if (flow === "update") {
                updateOnboardingState(state);
                goNext();
              }
              break;

            case PROGRESS_EVENT_TYPE.STEP_CHANGED:
              updateOnboardingState(state);
              router.replace(getOnboardingStepPath(state.currentStepName));
              break;
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") return;
        console.error("Onboarding stream failed:", error);
      }
    };

    subscribeToStream();

    return () => {
      resetOnboardingState();
      abortController.abort();
    };
  }, [
    onboardingState,
    updateOnboardingState,
    resetOnboardingState,
    goNext,
    router,
  ]);

  useEffect(() => {
    if (!stepNameFromPath) {
      return;
    }
    const viewKey = `${onboardingState.onboardingId}:${stepNameFromPath}`;
    if (lastViewedStep.current === viewKey) return;
    lastViewedStep.current = viewKey;
    track("onboarding_step_view", {
      stepName: stepNameFromPath,
      onboarding_id: onboardingState.onboardingId,
    });
  }, [stepNameFromPath, onboardingState.onboardingId]);

  return <>{children}</>;
};
